import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Pencil, MessageSquarePlus, XCircle, CheckCircle2, Trash2, Globe, Mail, Phone, User, AlertTriangle } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { SectionCard } from "@/components/common/SectionCard";
import { EmptyState } from "@/components/common/EmptyState";
import { StatusBadge } from "@/components/common/StatusBadge";
import { EntryModal } from "@/components/common/EntryModal";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { RelevanceScore } from "@/components/investor/RelevanceScore";
import { InteractionModal } from "@/components/investor/InteractionModal";
import { UpdatesHistory } from "@/components/product/UpdatesHistory";
import { investorFields } from "@/components/investor/investorFields";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { api } from "@/lib/api";
import { stageBadge, priorityBadge, gbp, DOCUMENT_LABELS, hasNoNextAction } from "@/data/investorOptions";

const today = () => new Date().toISOString().slice(0, 10);
const Row = ({ label, value }) => (
  <div><p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#5A6B8C]">{label}</p><p className="mt-0.5 whitespace-pre-wrap text-sm text-[#14245F]">{value?.toString().trim() ? value : <span className="text-[#5A6B8C]/70">Not set</span>}</p></div>
);
const fmt = (iso) => { try { return new Date(iso).toLocaleString(undefined, { day: "numeric", month: "short", year: "numeric" }); } catch { return iso; } };

export default function InvestorDetail() {
  const { investorId } = useParams();
  const navigate = useNavigate();
  const [inv, setInv] = useState(null);
  const [interactions, setInteractions] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [intOpen, setIntOpen] = useState(false);
  const [editingInt, setEditingInt] = useState(null);
  const [confirmDel, setConfirmDel] = useState(false);
  const [confirmIntDel, setConfirmIntDel] = useState(null);

  const load = useCallback(async () => {
    try {
      const [i, list] = await Promise.all([api.get("investors", investorId), api.list("investor-interactions")]);
      setInv(i); setInteractions(list.filter((x) => x.investor_id === investorId));
    } catch { setInv(null); }
  }, [investorId]);
  useEffect(() => { load(); }, [load]);

  if (!inv) {
    return <div className="py-20 text-center text-[#5A6B8C]" data-testid="investor-not-found"><p>Loading investor…</p><Button variant="outline" className="mt-4 border-[#E4E9F3]" onClick={() => navigate("/investors")}>Back to Investor Pipeline</Button></div>;
  }

  const handleEdit = async (v) => { await api.update("investors", investorId, v); toast.success("Investor updated"); await load(); };
  const patch = async (data, msg) => { await api.update("investors", investorId, data); toast.success(msg); await load(); };
  const handleDelete = async (reason) => { await api.remove("investors", investorId, reason); toast.success("Moved to Recycle Bin"); navigate("/investors"); };

  const toggleDoc = async (label) => {
    const docs = inv.documents_sent || [];
    const next = docs.includes(label) ? docs.filter((d) => d !== label) : [...docs, label];
    await api.update("investors", investorId, { documents_sent: next });
    setInv({ ...inv, documents_sent: next });
  };

  const interactionApi = {
    save: async (payload, id) => { if (id) { await api.update("investor-interactions", id, payload); toast.success("Interaction updated"); } else { await api.create("investor-interactions", payload); toast.success("Interaction added"); } await load(); },
    syncInvestor: async (p) => { await api.update("investors", investorId, p); toast.success("Investor updated"); await load(); },
  };

  const openAddInt = () => { setEditingInt(null); setIntOpen(true); };
  const openEditInt = (it) => { setEditingInt(it); setIntOpen(true); };
  const noAction = hasNoNextAction(inv);

  return (
    <>
      <button data-testid="back-to-investors" onClick={() => navigate("/investors")} className="mb-4 inline-flex items-center gap-1.5 text-sm text-[#5A6B8C] transition-colors hover:text-[#14245F]"><ArrowLeft size={16} /> Back to Investor Pipeline</button>

      <PageHeader
        eyebrow={inv.investor_type}
        title={inv.name}
        description={inv.thesis}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button data-testid="edit-investor-btn" onClick={() => setEditOpen(true)} className="rounded-full bg-[#1D4ED8] text-white hover:bg-[#1A45C0]"><Pencil size={15} /> Edit</Button>
            <Button variant="outline" data-testid="add-interaction-btn" onClick={openAddInt} className="border-[#E4E9F3]"><MessageSquarePlus size={15} /> Add Interaction</Button>
            {inv.stage !== "Passed / Not proceeding" && <Button variant="outline" data-testid="mark-passed-btn" onClick={() => patch({ stage: "Passed / Not proceeding" }, "Marked as passed")} className="border-[#E4E9F3]"><XCircle size={15} /> Mark Passed</Button>}
            {inv.stage !== "Invested" && <Button variant="outline" data-testid="mark-invested-btn" onClick={() => patch({ stage: "Invested" }, "Marked as invested")} className="border-[#E4E9F3] text-[#2D6A4F] hover:bg-[#2D6A4F]/10"><CheckCircle2 size={15} /> Mark Invested</Button>}
            <Button variant="outline" data-testid="delete-investor-btn" onClick={() => setConfirmDel(true)} className="border-[#E4E9F3] text-[#DC2626] hover:bg-[#DC2626]/10"><Trash2 size={15} /> Delete</Button>
          </div>
        }
      />

      {noAction && (
        <div className="mb-6 flex items-center gap-2 rounded-lg border border-[#C9A227]/40 bg-[#C9A227]/10 px-4 py-2.5 text-sm text-[#A16207]" data-testid="no-action-warning">
          <AlertTriangle size={16} /> This active investor has no next action set.
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <SectionCard testid="investor-detail-card" className="lg:col-span-2" title="Investor Details">
          <div className="mb-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-[#5A6B8C]">
            <StatusBadge status={stageBadge(inv.stage)} label={inv.stage} />
            {inv.migration_review_required && <StatusBadge status="warning" label="Migration review required" />}
            <StatusBadge status={priorityBadge(inv.priority)} label={inv.priority} />
            <RelevanceScore value={inv.relevance} />
            {inv.geography && <span>{inv.geography}</span>}
          </div>
          <div className="mb-4 flex flex-wrap gap-x-5 gap-y-1 text-sm text-[#14245F]">
            {inv.contact_person && <span className="inline-flex items-center gap-1.5"><User size={14} strokeWidth={1.5} /> {inv.contact_person}</span>}
            {inv.contact_email && <span className="inline-flex items-center gap-1.5"><Mail size={14} strokeWidth={1.5} /> {inv.contact_email}</span>}
            {inv.contact_phone && <span className="inline-flex items-center gap-1.5"><Phone size={14} strokeWidth={1.5} /> {inv.contact_phone}</span>}
            {inv.website && <a href={inv.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-[#C9A227] hover:underline"><Globe size={14} strokeWidth={1.5} /> Website</a>}
          </div>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
            <Row label="Potential amount" value={gbp(inv.potential_amount)} />
            <Row label="Probability" value={`${inv.probability || 0}%`} />
            <Row label="Next follow-up" value={inv.next_follow_up_date} />
            <Row label="Meeting date" value={inv.meeting_date} />
            <Row label="First contacted" value={inv.date_first_contacted} />
            <Row label="Last interaction" value={inv.last_interaction_date} />
          </div>
          <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
            <Row label="Next action" value={inv.next_action} />
            <Row label="Investment focus" value={inv.thesis} />
            <Row label="Relevance notes" value={inv.relevance_notes} />
            <Row label="Status summary" value={inv.status_summary} />
            <Row label="Source / referral" value={inv.source} />
            <Row label="Notes" value={inv.notes} />
            {inv.stage === "Passed / Not proceeding" && <Row label="Reason passed" value={inv.reason_passed} />}
          </div>
        </SectionCard>

        <SectionCard testid="documents-card" title="Documents Sent" subtitle="Track what's been shared">
          <div className="space-y-2" data-testid="documents-list">
            {DOCUMENT_LABELS.map((label) => {
              const checked = (inv.documents_sent || []).includes(label);
              return (
                <label key={label} className="flex cursor-pointer items-center gap-2.5 rounded-md px-1 py-1 text-sm text-[#14245F] hover:bg-[#F7F9FE]">
                  <Checkbox checked={checked} onCheckedChange={() => toggleDoc(label)} data-testid={`doc-${label.replace(/\s+/g, "-").toLowerCase()}`} className="border-[#E4E9F3] data-[state=checked]:bg-[#C9A227] data-[state=checked]:border-[#C9A227]" />
                  {label}
                </label>
              );
            })}
          </div>
        </SectionCard>
      </div>

      <SectionCard testid="interactions-card" className="mt-6" title="Interaction History" subtitle="Newest first"
        action={<Button size="sm" data-testid="interactions-add-btn" onClick={openAddInt} className="rounded-full bg-[#1D4ED8] text-white hover:bg-[#1A45C0]"><MessageSquarePlus size={15} /> Add</Button>}>
        {interactions.length === 0 ? (
          <EmptyState icon={MessageSquarePlus} title="No interactions yet" description="Log research notes, calls, emails and meetings here." actionLabel="Add interaction" onAction={openAddInt} />
        ) : (
          <ul className="space-y-3" data-testid="interactions-list">
            {interactions.map((it) => (
              <li key={it.id} data-testid={`interaction-${it.id}`} className="rounded-lg border border-[#E4E9F3] p-4">
                <div className="flex items-start justify-between gap-2">
                  <div><StatusBadge status="info" label={it.interaction_type} /><span className="ml-2 text-xs text-[#5A6B8C]">{it.author} · {fmt(it.date)}</span></div>
                  <div className="flex gap-1">
                    <Button size="icon" variant="ghost" data-testid={`edit-interaction-${it.id}`} onClick={() => openEditInt(it)} className="h-7 w-7 text-[#5A6B8C] hover:text-[#14245F]"><Pencil size={14} /></Button>
                    <Button size="icon" variant="ghost" data-testid={`delete-interaction-${it.id}`} onClick={() => setConfirmIntDel(it.id)} className="h-7 w-7 text-[#5A6B8C] hover:text-[#DC2626]"><Trash2 size={14} /></Button>
                  </div>
                </div>
                {it.summary && <p className="mt-2 whitespace-pre-wrap text-sm text-[#14245F]">{it.summary}</p>}
                {it.outcome && <p className="mt-1 text-xs text-[#5A6B8C]">Outcome: {it.outcome}</p>}
                {it.next_action && <p className="mt-1 text-xs text-[#5A6B8C]">→ Next: {it.next_action}{it.next_follow_up_date ? ` (by ${it.next_follow_up_date})` : ""}</p>}
              </li>
            ))}
          </ul>
        )}
      </SectionCard>

      <SectionCard testid="investor-updates-card" className="mt-6">
        <UpdatesHistory relatedId={investorId} relatedType="Investor" relatedName={inv.name} area="Investor" />
      </SectionCard>

      <EntryModal open={editOpen} onOpenChange={setEditOpen} title="Edit Investor" submitLabel="Save Changes" fields={investorFields()} initialValues={{ ...inv, relevance: String(inv.relevance) }} onSubmit={handleEdit} />
      <InteractionModal open={intOpen} onOpenChange={setIntOpen} investor={inv} editing={editingInt} onSaved={interactionApi} />
      <ConfirmDialog open={confirmDel} onOpenChange={setConfirmDel} title="Delete this investor?" onConfirm={handleDelete} softDelete />
      <ConfirmDialog open={!!confirmIntDel} onOpenChange={(o) => !o && setConfirmIntDel(null)} title="Delete this interaction?" description="This interaction will be permanently removed." onConfirm={async () => { await api.remove("investor-interactions", confirmIntDel); toast.success("Interaction deleted"); setConfirmIntDel(null); await load(); }} />
    </>
  );
}
